import { ReactElement, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  queryFactsheetComment,
  updateFactsheetCommentById,
} from 'src/screens/factsheets/factsheet/description/summary/factsheetSummaryQueries'
import { SummaryCommentEditForm, SummaryCommentFormValues } from 'src/screens/shared/common/SummaryCommentEditForm'
import { usePermissionsForFactsheetDetailsSummaryPage } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'

type originType = 'SAVE' | 'SAVE_AND_BACK'

export const FactsheetSummaryCommentEditPage = (): ReactElement => {
  const { factsheetId } = useParams()
  const factsheet_id = parseInt(factsheetId as string)

  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const originRef = useRef<originType>('SAVE')

  const [factsheetSummaryComment, setFactsheetSummaryComment] = useState<string | null>(null)

  const { loading, canEdit } = usePermissionsForFactsheetDetailsSummaryPage(factsheet_id)

  useEffect(() => {
    const initFormValues = async () => {
      const { data } = await urqlClient
        .query<{ factsheet: Query_Root['factsheet'] }>(queryFactsheetComment, {
          id: factsheet_id,
        })
        .toPromise()

      if (data && data.factsheet[0]) {
        const comment = data.factsheet[0].comment
        setFactsheetSummaryComment(comment || null)
      }
    }

    initFormValues()
  }, [factsheetId, urqlClient, notificationService])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const onBack = () => {
    navigate(ROUTES.FactsheetDescriptionRoot.nested.FactsheetSummary.params({ factsheetId }))
  }

  const updateHandler = async (values: SummaryCommentFormValues) => {
    const updateObject = {
      factsheetId: factsheet_id,
      comment: values.comment,
    }

    const { data, error } = await urqlClient
      .mutation<{ update_factsheet: Mutation_Root['update_factsheet'] }>(updateFactsheetCommentById, updateObject)
      .toPromise()

    if (error || data?.update_factsheet?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      const newComment = data.update_factsheet.returning[0].comment
      setFactsheetSummaryComment(newComment || null)

      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack()
      }
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.title.edit.comments')}
      onBack={onBack}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && (
            <>
              <HelpAndInstructions labelKey={`label.help.factsheet.summary.comments`} defaultExpansion />
              <SummaryCommentEditForm comment={factsheetSummaryComment} onSave={updateHandler} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
